import React, { useEffect, useRef } from 'react';
import PhoneInput from 'react-phone-input-2';

import InputBase from 'components/form-multi/InputBase';
import { clearErrorField, notValidateUpdateValue, validationAndUpdateValue } from 'components/form-multi/utils';

import { PHONE, PHONE_STRICT, TEL } from 'constants/FieldType';
import { PHONE_CUSTOM_MASK, EXCLUDE_COUNTRIES } from 'constants/utilsMasks';

import { inputPropType } from 'helpers/customPropTypes';
import { toUpperCase } from 'helpers/utils';

function useSearchCountry(ref, defaultCountry, { field, ...props }) {
    useEffect(() => {
        if (field.value) {
            ref.current.searchCountry(field.value);
            const { selectedCountry } = ref.current.state;

            if (selectedCountry && props.onChangeHandler) {
                props.onChangeHandler(field.value, { countryCode: selectedCountry.iso2 });
            }
        } else if (defaultCountry) {
            ref.current.updateCountry(defaultCountry);
        }
    }, []);
}

function useChangeDefaultCountry(ref, notFirstRender, defaultCountry, { field, formName, ...props }) {
    useEffect(() => {
        if (notFirstRender.current) {
            const { selectedCountry } = ref.current.state;
            if (!field.value || shouldUpdateCountryCode(defaultCountry, selectedCountry?.iso2)) {
                ref.current.updateCountry(defaultCountry);
            }
            if (props.changeFormFieldValue) {
                props.getUserInputWithoutPrefix(ref.current?.props.value.replace(selectedCountry.countryCode, ''));
                ref.current.value = props.userInputWithoutPrefix;
                props.changeFormFieldValue(formName, props.prefix?.replace(/\+/, '') + props.userInputWithoutPrefix, 'phone');
            }
        } else {
            notFirstRender.current = true;
        }
    }, [defaultCountry]);
}

// to solve the iso2 conflict caused by the country prefix difference (from React library & processing)
const shouldUpdateCountryCode = (defaultCountry, selectedCountry) => {
    if ((defaultCountry === 'do' && selectedCountry === 'us') || (defaultCountry === 'gb' && selectedCountry === 'je')) {
        return false;
    }
    return selectedCountry !== defaultCountry;
};

const NewInputPhoneHOC = ({ defaultCountry = 'gb', submitByEnter = false, focus = false, isFilled = true, isPhoneTypeStrict = true, ...props }) => {
    const ref = useRef(null);
    const notFirstRender = useRef(false);

    useSearchCountry(ref, defaultCountry, props);
    useChangeDefaultCountry(ref, notFirstRender, defaultCountry, props);
    const { field } = props;
    useEffect(() => {
        if (focus) {
            props.setIsFocused?.(true);
            props.setFocusOnLogin?.(false);
        }
    }, [focus]);

    const handlerKeyDown = (e) => {
        if (e.keyCode === 13 && submitByEnter && props.submitRef && props.submitRef.current) {
            const { selectedCountry } = ref.current?.state;
            const value = phoneNumberValidation(e);
            if (props.shouldValidateUserNumberFormat?.()) {
                props.validateNumberFormat?.(value, toUpperCase(props.iso2));
            } else {
                if (value !== selectedCountry.countryCode && props.submitNumberOnLogin) {
                    validationAndUpdateValue(props.formName, value, field, props.changeFormField)
                        .catch((validateEvent) => console.error(validateEvent))
                        .finally(() => props.submitNumberOnLogin());
                } else {
                    props.submitRef.current?.click();
                }
            }
        }
    };

    const handlerBlur = (e) => {
        const { selectedCountry } = ref.current?.state;
        if (!field.isError) {
            props.setIsFocused?.(false);
        }
        const value = phoneNumberValidation(e);
        if (value !== selectedCountry.countryCode && !props.shouldValidateUserNumberFormat?.()) {
            validationAndUpdateValue(props.formName, value, field, props.changeFormField)
                .then(() => props.onBlurHandler && props.onBlurHandler(field.value, field))
                .catch((blureValidateEvent) => console.error(blureValidateEvent));
        }
    };

    const phoneNumberValidation = (e) => {
        const { selectedCountry } = ref.current?.state;

        let value = e.target.value.replace(/\D+/g, '');
        if (props.userInputRef) {
            props.userInputRef.current = value;
        }
        if (props.getUserInputWithoutPrefix) {
            props.getUserInputWithoutPrefix(value.replace(selectedCountry.countryCode, ''));
        }
        return value;
    };

    const handlerFocus = () => {
        props.setIsFocused?.(true);
        const isError = field?.isError ?? false;
        if (isError) {
            clearErrorField(props.formName, field, props.changeFormField)
                .then(() => props.onFocus && props.onFocus())
                .catch((e) => console.error(e));
            props.setLoginInputError?.(false);
        }
    };

    const handlerChange = (value, data) => {
        if (!props.isFocused) {
            props.setIsFocused?.(true);
        }
        const pureValue = value.replace(/\D+/g, '');
        notValidateUpdateValue(props.formName, pureValue, field, props.changeFormField)
            .then((phone) => props.onChangeHandler && props.onChangeHandler(phone.value, data, ref?.current?.state?.selectedCountry))
            .catch((e) => console.error(e));
    };

    return (
        <PhoneInput
            ref={ref}
            inputProps={{
                name: field.name,
                autoFocus: focus,
                autoComplete: 'tel',
            }}
            specialLabel={''}
            value={field.value || ''}
            disableDropdown={true}
            countryCodeEditable={!isPhoneTypeStrict}
            placeholder={''}
            formName={field.canonicalPath || field.name}
            isFocused={props.isFocused}
            defaultCountry={defaultCountry}
            defaultMask={'... ... ... ... ..'}
            masks={PHONE_CUSTOM_MASK}
            excludeCountries={EXCLUDE_COUNTRIES}
            isFilled={props.isFilled}
            onChange={handlerChange}
            onKeyDown={handlerKeyDown}
            onFocus={handlerFocus}
            onBlur={handlerBlur}
            enableLongNumbers={16}
            enableTerritories={true}
        />
    );
};

const NewInputPhone = InputBase(NewInputPhoneHOC);

NewInputPhone.propTypes = inputPropType([PHONE, PHONE_STRICT, TEL]);

export default NewInputPhone;
